/**
 * Sale Order Agent API Client
 * Completely separate from V2ApiClient - uses /api/sale/workflows endpoints
 */
class SaleApiClient {
  constructor(token) {
    this.token = token;
    this.baseUrl = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
    this.apiRoute = '/api/sale/workflows';
  }

  /**
   * Start a new Sale Order workflow execution
   */
  async startWorkflow(workflowId, userMessage) {
    console.log('🚀 SaleAPI: Starting workflow', { workflowId, userMessage });

    const response = await fetch(`${this.baseUrl}${this.apiRoute}/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`
      },
      body: JSON.stringify({
        workflow_id: parseInt(workflowId),
        user_message: userMessage,
        token: this.token
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (!data.success) {
      throw new Error(data.error || 'Failed to start workflow');
    }

    console.log('✅ SaleAPI: Workflow started', data);
    return data;
  }

  /**
   * Continue an existing Sale Order workflow execution
   */
  async continueWorkflow(runId, userMessage) {
    console.log('🔄 SaleAPI: Continuing workflow', { runId, userMessage });

    const response = await fetch(`${this.baseUrl}${this.apiRoute}/continue/${runId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`
      },
      body: JSON.stringify({
        user_message: userMessage,
        token: this.token
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (!data.success) {
      throw new Error(data.error || 'Failed to continue workflow');
    }

    console.log('✅ SaleAPI: Workflow continued', data);
    return data;
  }

  /**
   * Get workflow execution status and details
   */
  async getWorkflowStatus(runId) {
    console.log('📊 SaleAPI: Getting workflow status', { runId });

    const response = await fetch(`${this.baseUrl}${this.apiRoute}/status/${runId}`, {
      headers: {
        'Authorization': `Bearer ${this.token}`
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    console.log('✅ SaleAPI: Got workflow status', data);
    return data;
  }

  /**
   * Get detailed execution information for debugging
   */
  async getExecutionDetails(runId) {
    console.log('🔍 SaleAPI: Getting execution details', { runId });

    const response = await fetch(`${this.baseUrl}${this.apiRoute}/${runId}/execution-details`, {
      headers: {
        'Authorization': `Bearer ${this.token}`
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    console.log('✅ SaleAPI: Got execution details', data);
    return data;
  }

  /**
   * Get workflow events and chat messages
   */
  async getWorkflowEvents(runId, since = null) {
    console.log('📞 SaleAPI: Getting workflow events', { runId, since });

    let url = `${this.baseUrl}${this.apiRoute}/${runId}/events`;
    if (since) {
      url += `?since=${encodeURIComponent(since)}`;
    }

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${this.token}`
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    console.log('✅ SaleAPI: Got workflow events', data);
    return data;
  }
}

export default SaleApiClient;
